'use client';

import {
  type ReactNode,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { LayoutManager } from '../layout-managers/LayoutManager';

const STICKY_TEST_ID = 'flash-list-sticky';

export interface StickyHeadersProps<T> {
  scrollerRef: RefObject<HTMLDivElement | null>;
  stickyHeaderIndices: number[];
  data: ReadonlyArray<T>;
  layoutManager: LayoutManager;
  renderItem: (info: {
    item: T;
    index: number;
    target: 'StickyHeader';
  }) => ReactNode;
}

/**
 * Web port of upstream FlashList's StickyHeaders component
 * (src/recyclerview/components/StickyHeaders.tsx). Renders a single
 * absolute-positioned overlay above the scrollable area; tracks the
 * scroll-position-derived "active" sticky index via a binary search on
 * the sorted indices, and animates a "push" up as the next sticky's
 * top approaches the current sticky's bottom.
 *
 * Two important pragmatic divergences from upstream:
 *   1. We attach a DOM scroll listener directly so the overlay updates
 *      without participating in React renders for the push animation
 *      (only the active-index transition triggers a re-render). Upstream
 *      uses Animated.Value for the same effect on RN.
 *   2. Inverted + sticky is unsupported for now — defer until a real
 *      consumer asks for it.
 */
export function StickyHeaders<T>({
  scrollerRef,
  stickyHeaderIndices,
  data,
  layoutManager,
  renderItem,
}: StickyHeadersProps<T>) {
  const sortedIndices = useMemo(
    () =>
      [...stickyHeaderIndices].sort(
        (first, second) => first - second,
      ) as readonly number[],
    [stickyHeaderIndices],
  );
  const sortedIndicesRef = useRef(sortedIndices);
  sortedIndicesRef.current = sortedIndices;

  const dataRef = useRef(data);
  dataRef.current = data;

  const layoutManagerRef = useRef(layoutManager);
  layoutManagerRef.current = layoutManager;

  const [activeIndex, setActiveIndex] = useState(-1);
  const activeIndexRef = useRef(-1);
  activeIndexRef.current = activeIndex;

  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const update = () => {
      const indices = sortedIndicesRef.current;
      if (indices.length === 0) {
        if (activeIndexRef.current !== -1) {
          setActiveIndex(-1);
          activeIndexRef.current = -1;
        }
        return;
      }
      const scrollOffset = scroller.scrollTop;
      const lm = layoutManagerRef.current;

      // Binary search: last index where layout.y <= scrollOffset.
      let lo = 0;
      let hi = indices.length - 1;
      let resultPosition = -1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        const candidate = indices[mid];
        if (candidate >= dataRef.current.length) {
          hi = mid - 1;
          continue;
        }
        const y = lm.getLayout(candidate).y;
        if (y <= scrollOffset) {
          resultPosition = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }

      const newActiveIndex =
        resultPosition === -1 ? -1 : indices[resultPosition];

      if (newActiveIndex !== activeIndexRef.current) {
        activeIndexRef.current = newActiveIndex;
        setActiveIndex(newActiveIndex);
      }

      // Push animation: when the next sticky's top approaches the current
      // sticky's bottom, translate the overlay up by the overlap so the
      // current sticky pushes off-screen smoothly.
      const overlay = overlayRef.current;
      if (!overlay) return;
      if (newActiveIndex === -1) {
        overlay.style.transform = '';
        return;
      }
      const nextStickyIndex = indices[resultPosition + 1];
      if (
        nextStickyIndex == null ||
        nextStickyIndex >= dataRef.current.length
      ) {
        overlay.style.transform = '';
        return;
      }
      const currentLayout = lm.getLayout(newActiveIndex);
      const nextLayout = lm.getLayout(nextStickyIndex);
      const pushStartsAt = nextLayout.y - currentLayout.height;
      const overlapStart = pushStartsAt;
      const overlapEnd = nextLayout.y;
      if (scrollOffset >= overlapStart && scrollOffset < overlapEnd) {
        const progress = (scrollOffset - overlapStart) / currentLayout.height;
        overlay.style.transform = `translateY(${-progress * currentLayout.height}px)`;
      } else {
        overlay.style.transform = '';
      }
    };

    scroller.addEventListener('scroll', update, { passive: true });
    update();
    return () => scroller.removeEventListener('scroll', update);
  }, [scrollerRef]);

  // Recompute when stickyHeaderIndices or data changes (e.g. items added).
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.dispatchEvent(new Event('scroll'));
  }, [sortedIndices, data, scrollerRef]);

  if (activeIndex === -1 || activeIndex >= data.length) {
    return null;
  }

  return (
    <div
      ref={overlayRef}
      data-testid={STICKY_TEST_ID}
      style={{
        // `position: sticky` pins the element to the top of its nearest
        // scrollable ancestor — that's the FlashList scroller. `height: 0`
        // keeps it from displacing the items below; the actual sticky cell
        // renders inside the absolute-positioned child so it overlays the
        // list content.
        position: 'sticky',
        top: 0,
        height: 0,
        zIndex: 2,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
        }}
      >
        {renderItem({
          item: data[activeIndex],
          index: activeIndex,
          target: 'StickyHeader',
        })}
      </div>
    </div>
  );
}
